/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the class responsible for managing the messaging between the
 * app and the parent window.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * adds ability to create event listeners
 */
import { addEventListener } from 'consolidated-events';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Typings
 */
import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Enumerations
 * ---------------------------------------------------------------------------------
 */

export enum WindowView {
    Search = 1,
    Favourites = 2,
    Browse = 3,
    Error = 4,
    Introduction = 5
}

enum WindowReceivedEvent {
    Navigate = 1
}

enum WindowSentEvent {
    Open = 1,
    Cancel = 2,
    Select = 3,
    Navigate = 4
}

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IAppState {
    view: WindowView;
    selection: IAppSelection;
}

interface IAppSelection {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    term: Dtos.Term | null;
    subterm: Dtos.Term | null;
    grade: Dtos.Grade | null;
    specification: string | null;
}

interface INavigateData {
    versionId: number;
    categoryId: number;
    termId: number;
    gradeId: number;
}

interface IWindowEvent<EventData> {
    type: WindowReceivedEvent;
    instanceId: string;
    payload: EventData;
}

interface IWindowMessage<EventData> {
    data?: IWindowEvent<EventData>;
    source?: Window;
    origin?: string;
}

interface IMessageManagerOptions {
    instanceId?: string;
    parentOrigin?: string;
}

type IMessageSubscription = (event: IWindowEvent<any>) => void;
type INavigateMessageSubscription = (navigateData: INavigateData) => void;

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/**
 * This theme manages the loading and swapping of themes in the application.
 */
class MessageManager {
    private instanceId: string | undefined;
    private removeSubscription: () => void;
    private parentOrigin: string;
    private messagingEnabled: boolean;
    private sentOpenMessage: boolean;
    private subscriptions: IMessageSubscription[];
    private navigateSubscriptions: INavigateMessageSubscription[];

    constructor(options?: IMessageManagerOptions)
    {
        this.messagingEnabled = false;

        // bind subscription runner scope.
        this.runSubscription = this.runSubscription.bind(this);

        // initialise subscriptions
        this.subscriptions = [];

        // bind subscription runner scope.
        this.runNavigateSubscription = this.runNavigateSubscription.bind(this);

        // initialise subscriptions
        this.navigateSubscriptions = [];

        if (!options) {
            return;
        }

        this.instanceId = options.instanceId;

        if (!options.parentOrigin || (!window.opener && !window.parent)) {
            return;
        }

        this.messagingEnabled = true;

        this.parentOrigin = options.parentOrigin;

        this.handleMessageFromParent = this.handleMessageFromParent.bind(this);

        this.removeSubscription = addEventListener(
            window,
            'message',
            this.handleMessageFromParent
        );
    }

    public hasParentWindow() {
        return this.messagingEnabled;
    }

    /**
     * This function registers a subscription through the use of a callback function.
     * @param subscription subscription callback.
     */
    public subscribe(subscription: IMessageSubscription) {
        // check a callback was passed otherwise the subscription is pointless.
        if (!subscription) {
            throw new Error(`A function is required to register a subscription.`);
        }

        // register subscription.
        this.subscriptions.push(subscription);

        // return unsubscribe function.
        return () => {
            this.subscriptions = this.subscriptions.filter(sub => sub !== subscription);
        };
    }

    /**
     * This function registers a subscription through the use of a callback function.
     * @param subscription subscription callback.
     */
    public subscribeToNavigate(subscription: INavigateMessageSubscription) {
        // check a callback was passed otherwise the subscription is pointless.
        if (!subscription) {
            throw new Error(`A function is required to register a subscription.`);
        }

        // register subscription.
        this.navigateSubscriptions.push(subscription);

        // return unsubscribe function.
        return () => {
            this.navigateSubscriptions = this.navigateSubscriptions.filter(sub => sub !== subscription);
        };
    }

    public updateAppState(appState: IAppState) {
        if (this.sentOpenMessage) {
            this.postMessage({
                type: WindowSentEvent.Navigate,
                instanceId: this.instanceId,
                payload: appState
            });
        }
        else {
            this.postMessage({
                type: WindowSentEvent.Open,
                instanceId: this.instanceId,
                payload: appState
            });

            this.sentOpenMessage = true;
        }
    }

    public select(appState: IAppState) {
        this.postMessage({
            type: WindowSentEvent.Select,
            instanceId: this.instanceId,
            payload: appState
        });
    }

    public dispose() {
        this.removeSubscription();
    }

    private postMessage<MessageDataType>(data: MessageDataType) {
        if (this.messagingEnabled &&
            this.parentOrigin) {
                if (window.opener) {
                    window.opener.postMessage(
                        data,
                        this.parentOrigin
                    );
                }
                else if (window.parent) {
                    window.parent.postMessage(
                        data,
                        this.parentOrigin
                    );
                }
            }
    }

    private handleMessageFromParent(message: IWindowMessage<any>) {
        if (!message ||
            message.origin !== this.parentOrigin ||
            !message.data ||
            message.data.instanceId !== this.instanceId ||
            !message.data.type) {
                return;
            }

        this.runSubscriptions(message.data);

        switch (message.data.type) {
            case WindowReceivedEvent.Navigate:
                this.handleNavigateEvent(message.data.payload);
                break;
        }
    }

    private handleNavigateEvent(payload: INavigateData) {
        this.runNavigateSubscriptions(payload);
    }

    /**
     * This function runs all subscription callbacks.
     */
    private runSubscriptions(event: IWindowEvent<any>) {
        this.subscriptions.forEach((sub) => this.runSubscription(event, sub));
    }

    /**
     * This function runs a subscription callback.
     */
    private runSubscription(event: IWindowEvent<any>, subscription: IMessageSubscription) {
        subscription(event);
    }

    /**
     * This function runs all subscription callbacks.
     */
    private runNavigateSubscriptions(navigateData: INavigateData) {
        this.navigateSubscriptions.forEach((sub) => this.runNavigateSubscription(navigateData, sub));
    }

    /**
     * This function runs a subscription callback.
     */
    private runNavigateSubscription(navigateData: INavigateData, navigateSubscription: INavigateMessageSubscription) {
        navigateSubscription(navigateData);
    }
}

export default MessageManager;


// WEBPACK FOOTER //
// ./src/utils/MessageManager.ts